import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { Subject, Subscription } from 'rxjs'
import { finalize, takeUntil } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { AccessState } from 'src/app/services/access.service'
import { StacksetsRegenerationPollService } from '../services/stacksets-regeneration-poll.service'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'
import {
  StacksetsConfig,
  StacksetsConfigCacheService,
  StacksetsManagementTopology,
  createDefaultStacksetsConfig,
} from '../services/stacksets-config-cache.service'

@Component({
  selector: 'stacksets-update-instructions',
  templateUrl: './stacksets-update-instructions.component.html',
  styleUrls: ['./stacksets-update-instructions.component.scss'],
  standalone: false,
  providers: [CopyFeedbackService],
})
export class StacksetsUpdateInstructionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sourceStackSetTemplateUrl: string
  @Input() targetStackSetTemplateUrl: string
  @Input() sourceAccountId: string
  @Input() sourceRegion: string
  @Input() targetAccountId: string
  @Input() targetRegion: string

  // Access state inputs
  @Input() sourceStackName: string
  @Input() targetStackName: string
  @Input() sourceManagedByStackSet: boolean = false
  @Input() targetManagedByStackSet: boolean = false

  // Access testing inputs
  @Input() sourceAccessState: AccessState
  @Input() targetAccessState: AccessState
  @Input() sourceTestCount: number = 0
  @Input() targetTestCount: number = 0

  // Output for test access
  @Output() testAccess = new EventEmitter<'source' | 'target'>()

  // Management account ID (if known, passed from parent)
  @Input() managementAccountId: string = ''

  // Arpio account ID and regeneration job ID — both required to poll. The whole
  // command UI is gated by regenerationInProgress: unlike the setup flow's Step 3,
  // the update command needs ALL sync pairs' templates regenerated, since one
  // update-stack-set call rolls out the new templates everywhere at once.
  @Input() arpioAccountId: string = ''
  @Input() regenerationJobId: string | null = null

  // User-entered StackSets values, backed by the per-Arpio-account localStorage cache.
  // The cache pre-fills these on init from anything the user entered previously for
  // this account (in either setup or update flow), and every mutation here mirrors back
  // to it. Convenience cache only — not authoritative state (see StacksetsConfigCacheService).
  private config: StacksetsConfig = createDefaultStacksetsConfig()

  regenerationInProgress = false
  private regenerationPollSub: Subscription | null = null
  private destroy$ = new Subject<void>()

  constructor(
    public uiService: UiService,
    public copyFeedback: CopyFeedbackService,
    private regenerationPollService: StacksetsRegenerationPollService,
    private configCache: StacksetsConfigCacheService
  ) {}

  ngOnInit(): void {
    const cached = this.configCache.load(this.arpioAccountId)
    if (cached) this.config = cached
  }

  // Per-field accessors keep `[(ngModel)]` bindings clean and fan each write out to
  // `afterConfigChange()` so the cache stays in sync.
  get managementTopology(): StacksetsManagementTopology {
    return this.config.managementTopology
  }
  set managementTopology(value: StacksetsManagementTopology) {
    this.config.managementTopology = value
    this.afterConfigChange()
  }

  get primaryManagementAccountId(): string {
    return this.config.primaryManagementAccountId
  }
  set primaryManagementAccountId(value: string) {
    this.config.primaryManagementAccountId = value
    this.afterConfigChange()
  }

  get recoveryManagementAccountId(): string {
    return this.config.recoveryManagementAccountId
  }
  set recoveryManagementAccountId(value: string) {
    this.config.recoveryManagementAccountId = value
    this.afterConfigChange()
  }

  get primaryStackSetName(): string {
    return this.config.primaryStackSetName
  }
  set primaryStackSetName(value: string) {
    this.config.primaryStackSetName = value
    this.afterConfigChange()
  }

  get recoveryStackSetName(): string {
    return this.config.recoveryStackSetName
  }
  set recoveryStackSetName(value: string) {
    this.config.recoveryStackSetName = value
    this.afterConfigChange()
  }

  get isTwoMgmtAccounts(): boolean {
    return this.config.managementTopology === 'two'
  }

  onSingleMgmtToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked
    this.managementTopology = checked ? 'one' : 'two'
  }

  private afterConfigChange(): void {
    this.configCache.save(this.arpioAccountId, this.config)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['regenerationJobId'] || changes['arpioAccountId']) {
      this.refreshRegenerationPoll()
    }
  }

  ngOnDestroy(): void {
    this.stopRegenerationPoll()
    this.destroy$.next()
    this.destroy$.complete()
  }

  private refreshRegenerationPoll(): void {
    this.stopRegenerationPoll()

    if (!this.regenerationJobId || !this.arpioAccountId) {
      this.regenerationInProgress = false
      return
    }

    this.regenerationInProgress = true

    this.regenerationPollSub = this.regenerationPollService
      .pollUntilDone(this.arpioAccountId, this.regenerationJobId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.regenerationInProgress = false
          this.regenerationPollSub = null
        })
      )
      .subscribe()
  }

  private stopRegenerationPoll(): void {
    if (this.regenerationPollSub) {
      this.regenerationPollSub.unsubscribe()
      this.regenerationPollSub = null
    }
  }

  // Get unique account IDs (avoids showing duplicates when source and target are same account)
  get uniqueAccountIds(): string[] {
    const accounts = new Set<string>()
    if (this.sourceAccountId) accounts.add(this.sourceAccountId)
    if (this.targetAccountId) accounts.add(this.targetAccountId)
    return Array.from(accounts)
  }

  onCopied(commandId: string): void {
    this.copyFeedback.onCopied(commandId)
  }

  isCopied(commandId: string): boolean {
    return !!this.copyFeedback.copiedStates[commandId]
  }

  // Download command as shell script
  downloadCommand(type: 'primary' | 'recovery'): void {
    const command = type === 'primary' ? this.primaryStackSetCommand : this.recoveryStackSetCommand
    const content = `#!/bin/bash\n\n${command}\n`
    const blob = new Blob([content], { type: 'text/x-sh' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `arpio-stackset-${type}.sh`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Resolved management account placeholders. In single-mgmt topology both sides resolve
  // to the same account; in two-mgmt topology each side resolves to its own. The
  // `managementAccountId` @Input is a pre-fill hint from the parent and is only used
  // when the user hasn't typed anything yet.
  get primaryMgmtAccountPlaceholder(): string {
    return this.primaryManagementAccountId || this.managementAccountId || '<YOUR-MANAGEMENT-ACCOUNT-ID>'
  }

  get recoveryMgmtAccountPlaceholder(): string {
    if (this.isTwoMgmtAccounts) {
      return this.recoveryManagementAccountId || '<YOUR-RECOVERY-MANAGEMENT-ACCOUNT-ID>'
    }
    return this.primaryMgmtAccountPlaceholder
  }

  // Helpers for StackSet name placeholders
  get primaryStackSetNameValue(): string {
    return this.primaryStackSetName || '<YOUR-PRIMARY-STACKSET-NAME>'
  }

  get recoveryStackSetNameValue(): string {
    return this.recoveryStackSetName || '<YOUR-RECOVERY-STACKSET-NAME>'
  }

  // Check if StackSets are already configured (at least one stack managed by StackSet)
  get stackSetsConfigured(): boolean {
    return this.sourceManagedByStackSet || this.targetManagedByStackSet
  }

  // Check if this is a fresh setup (no StackSets configured at all)
  get isSetupMode(): boolean {
    return !this.sourceManagedByStackSet && !this.targetManagedByStackSet
  }

  // Access testing helpers
  get isTestingSource(): boolean {
    return this.sourceAccessState === AccessState.TESTING
  }

  get isTestingTarget(): boolean {
    return this.targetAccessState === AccessState.TESTING
  }

  get sourceIsLatest(): boolean {
    return this.sourceAccessState === AccessState.SUCCESS
  }

  get targetIsLatest(): boolean {
    return this.targetAccessState === AccessState.SUCCESS
  }

  get sourceRetryText(): string {
    if (!this.sourceTestCount) return ''
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return textOptions[this.sourceTestCount % textOptions.length]
  }

  get targetRetryText(): string {
    if (!this.targetTestCount) return ''
    const textOptions = ["It's still not working.", "Hmm... It's not working yet."]
    return textOptions[this.targetTestCount % textOptions.length]
  }

  onTestAccess(scope: 'source' | 'target'): void {
    this.testAccess.emit(scope)
  }

  // ==================== COMMAND GENERATORS ====================

  // Unified command getters that switch between create/update based on mode
  get primaryStackSetCommand(): string {
    if (this.isSetupMode) {
      return this.createPrimaryStackSetCommand
    }
    return this.updatePrimaryStackSetCommand
  }

  get recoveryStackSetCommand(): string {
    if (this.isSetupMode) {
      return this.createRecoveryStackSetCommand
    }
    return this.updateRecoveryStackSetCommand
  }

  // Create commands (for setup mode)
  get createPrimaryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --template-url ${this.sourceStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.primaryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --permission-model SELF_MANAGED`
  }

  get createRecoveryStackSetCommand(): string {
    return `aws cloudformation create-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --template-url ${this.targetStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.recoveryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --execution-role-name ArpioStackSetExecutionRole \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --permission-model SELF_MANAGED`
  }

  // Update commands (for update mode)
  get updatePrimaryStackSetCommand(): string {
    return `aws cloudformation update-stack-set \\
  --stack-set-name ${this.primaryStackSetNameValue} \\
  --template-url ${this.sourceStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.primaryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }

  get updateRecoveryStackSetCommand(): string {
    return `aws cloudformation update-stack-set \\
  --stack-set-name ${this.recoveryStackSetNameValue} \\
  --template-url ${this.targetStackSetTemplateUrl} \\
  --administration-role-arn arn:aws:iam::${this.recoveryMgmtAccountPlaceholder}:role/ArpioStackSetAdministrationRole \\
  --capabilities CAPABILITY_NAMED_IAM`
  }
}
